import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClinicApiService } from "../api/ClinicApiService";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";

const ClinicDetailScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [clinic, setClinic] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const clinicApi = new ClinicApiService();

  useEffect(() => {
    loadClinicData();
  }, []);

  const loadClinicData = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await clinicApi.getMyClinic();
      if (result.isSuccess) {
        if (result.data) {
          setClinic(result.data);
        } else {
          // No clinic found - redirect to home
          navigate("/home");
        }
      } else {
        setError(result.error || "Failed to load clinic");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load clinic");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }


  return (
    <ResponsiveFormLayout
      showLanguageToggle={true}
      icon="🏥"
      iconSize="w-24 h-24 text-6xl"
      title={clinic.name || t("clinic_details")}
      subtitle={`${t("status")}: ${clinic.status || "Active"}`}
      maxHeight="90vh"
      onBack={() => navigate("/home")}
      showBackButton={true}
    >
      <div className="w-full space-y-6">
        {/* Clinic Information */}
        <div className="p-5 rounded-xl border border-gray-200 bg-gray-50">
          <h3 className={`text-lg font-semibold mb-4 ${isRTL ? "text-right" : "text-left"}`}>
            {t("clinic_information") || "Clinic Information"}
          </h3>
          <div className="space-y-3">
            {clinic.governorate && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("governorate")}:</span>
                <span className="text-sm font-medium">{clinic.governorate}</span>
              </div>
            )}
            {clinic.area && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("city")}:</span>
                <span className="text-sm font-medium">{isRTL ? clinic.area.name_ar : clinic.area.name}</span>
              </div>
            )}
            {clinic.neighborhood && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("neighborhood")}:</span>
                <span className="text-sm font-medium">{clinic.neighborhood}</span>
              </div>
            )}
            {clinic.street && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("street")}:</span>
                <span className="text-sm font-medium">{clinic.street}</span>
              </div>
            )}
          </div>
        </div>

        {/* Clinic Actions */}
        <div className="space-y-3">
          <h3 className={`text-lg font-semibold ${isRTL ? "text-right" : "text-left"}`}>
            {t("clinic_actions") || "Clinic Actions"}
          </h3>

          {/* Renew License - Coming Soon */}
          <button
            disabled
            className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 text-gray-400 text-base font-semibold opacity-60 cursor-not-allowed"
          >
            🔄 {t("renew_license") || "Renew License"} ({t("coming_soon") || "Coming Soon"})
          </button>

          {/* Relocate Clinic - Coming Soon */}
          <button
            disabled
            className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 text-gray-400 text-base font-semibold opacity-60 cursor-not-allowed"
          >
            📍 {t("relocate_clinic") || "Relocate Clinic"} ({t("coming_soon") || "Coming Soon"})
          </button>

          {/* Close Clinic - Coming Soon */}
          <button
            disabled
            className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 text-gray-400 text-base font-semibold opacity-60 cursor-not-allowed"
          >
            ❌ {t("close_clinic") || "Close Clinic"} ({t("coming_soon") || "Coming Soon"})
          </button>
        </div>

        {/* Register New Clinic */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={() => navigate("/home")}
            className="w-full px-6 py-4 rounded-2xl bg-[#2E5D7D] text-white text-base font-semibold hover:opacity-90 transition-all"
          >
            + {t("register_new_clinic") || "Register New Clinic"}
          </button>
        </div>
      </div>
    </ResponsiveFormLayout>
  );
};

export default ClinicDetailScreen;
